import React, {
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Switch } from "@mui/material";
import { DownOutlined, PlusCircleFilled } from "@ant-design/icons";
import styles from "./styles/AssetEvaluation.module.css";
import Info from "../Info/info";
import EvaluationChart from "./EvaluationChart";
import Dropdown from "./Dropdown";
import CountryPicker from "./CountryPicker";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import { formatStoredDate } from "../../../Utils/formatStoredDate";
import ReportDateField from "../Asset Dashboard/ReportDateField";
import { UserData } from "../../../Context/DataContext";
import {
  fetchCurrencyRates,
  convertAmount,
  displayAmount,
  formatCompactMoney,
  missingDebtFields,
  OWNER_CUSTOM,
  sortRecordsByDate,
} from "../../../Utils/assetEvaluationUtils";

/*
  Asset Evaluation — shared by the Add Asset and Edit Asset modals.

  Frontend only for now: this component owns its state and reports upwards via
  `onChange`. It is deliberately NOT merged into the asset payload the modals
  submit, so nothing here can affect the existing save flow until the backend
  endpoints exist. See docs/asset-evaluation-backend-spec.md for the field
  contract handed to the backend engineer.
*/

// Values follow the existing convention in My Account/activateAccount.jsx,
// where currencies are stored lowercase ("eur", "usd").
export const CURRENCY_OPTIONS = [
  { value: "eur", label: "EUR", symbol: "€" },
  { value: "usd", label: "USD", symbol: "$" },
  { value: "gbp", label: "GBP", symbol: "£" },
  { value: "chf", label: "CHF", symbol: "CHF" },
  { value: "cad", label: "CAD", symbol: "CA$" },
  { value: "aud", label: "AUD", symbol: "A$" },
  { value: "jpy", label: "JPY", symbol: "¥" },
  { value: "cny", label: "CNY", symbol: "CN¥" },
];

/*
  The currency a picker starts on, taken from the site language — the same rule
  the language provider already applies to its "€" / "$" symbol
  (Context/changeLanguageProvider.js): French means EUR, anything else USD. A
  default only; every currency above stays selectable.
*/
export const defaultCurrencyFor = (languageLabel) =>
  languageLabel === "fr" ? "eur" : "usd";

/*
  Pre-configured list, shared by every user. Labels come from the language
  files so they translate. A user's own saved custom types are passed in via
  the `customExposureOptions` prop and rendered after these.
*/
export const FINANCIAL_EXPOSURE_OPTIONS = [
  { value: "crypto", labelKey: "assetEvaluationText19" },
  { value: "real_estate", labelKey: "assetEvaluationText20" },
  { value: "stocks", labelKey: "assetEvaluationText21" },
  { value: "bonds", labelKey: "assetEvaluationText22" },
  { value: "private_equity", labelKey: "assetEvaluationText23" },
];

// Sentinel: selecting this reveals the free-text "New field" input.
export const FINANCIAL_EXPOSURE_CUSTOM = "custom";

/*
  Type of Debt, as two groups. The headings are rendered by `Dropdown` as plain
  labels with no click handler, so only the values below can ever be chosen —
  there is no sentinel to guard against.
*/
export const DEBT_TYPE_GROUPS = [
  {
    labelKey: "assetEvaluationText57",
    options: [
      { value: "primary_residence_mortgage", labelKey: "assetEvaluationText59" },
      { value: "investment_property_mortgage", labelKey: "assetEvaluationText60" },
      { value: "commercial_mortgage", labelKey: "assetEvaluationText61" },
      { value: "auto_vehicle_loan", labelKey: "assetEvaluationText62" },
      { value: "margin_lombard_loan", labelKey: "assetEvaluationText63" },
      { value: "business_asset_loan", labelKey: "assetEvaluationText64" },
    ],
  },
  {
    labelKey: "assetEvaluationText58",
    options: [
      { value: "personal_loan", labelKey: "assetEvaluationText65" },
      { value: "credit_card_debt", labelKey: "assetEvaluationText66" },
      { value: "overdraft", labelKey: "assetEvaluationText67" },
      { value: "student_loan", labelKey: "assetEvaluationText68" },
      { value: "tax_liability", labelKey: "assetEvaluationText69" },
      { value: "private_family_loan", labelKey: "assetEvaluationText70" },
      { value: "general_business_debt", labelKey: "assetEvaluationText71" },
      { value: "other", labelKey: "assetEvaluationText72" },
    ],
  },
];

// "New Field" block types.
export const DATA_ENTRY_TYPES = [
  { value: "other_owners", labelKey: "assetEvaluationText36" },
  { value: "location", labelKey: "assetEvaluationText79" },
  { value: "annual_income", labelKey: "assetEvaluationText82" },
  { value: "monthly_income", labelKey: "assetEvaluationText83" },
  { value: "custom", labelKey: "assetEvaluationText46" },
];

// The two that are an amount in a currency, handled together throughout.
export const INCOME_ENTRY_TYPES = ["annual_income", "monthly_income"];

/*
  Every block type describes ONE fact about the asset, so an asset can only
  carry one of each: it cannot sit in two countries at once, and a second
  annual income would just be a rival answer to the same question.

  "Custom" is the exception — it exists precisely so the user can name facts we
  did not anticipate, and there is no limit on how many of those there are.

  A type already spoken for stays IN the list, disabled rather than removed, so
  the menu reads the same every time and the user can see that the option exists
  and is already used, instead of wondering where it went.
*/
export const REPEATABLE_ENTRY_TYPES = ["custom"];

export const takenEntryTypes = (entries, exceptId) =>
  new Set(
    (Array.isArray(entries) ? entries : [])
      .filter(
        (entry) =>
          entry &&
          entry.id !== exceptId &&
          entry.type &&
          !REPEATABLE_ENTRY_TYPES.includes(entry.type)
      )
      .map((entry) => entry.type)
  );

export const MAX_OWNERS = 10;
export const TOTAL_OWNERSHIP = 100;

// Ids only need to be unique within the form; the backend assigns real ones.
let localIdSeed = 0;
const nextLocalId = (prefix) => `${prefix}_${(localIdSeed += 1)}`;

export const makeOwner = () => ({
  id: nextLocalId("owner"),
  owner: "",
  ownership: "",
});

export const makeCustomField = () => ({
  id: nextLocalId("cf"),
  title: "",
  value: "",
});

/*
  `type` is the block's opening selection. It defaults to the first entry in
  DATA_ENTRY_TYPES rather than a hard-coded name, so the two cannot drift apart
  if that list is ever reordered — and the caller overrides it when that type is
  already spoken for; see addDataEntry.
*/
export const makeDataEntry = (type = DATA_ENTRY_TYPES[0].value) => ({
  id: nextLocalId("entry"),
  type,
  my_ownership: "",
  owners: [makeOwner()], // there is always at least one other owner
  custom_fields: [makeCustomField()], // ...and always at least one custom field
});

export const EMPTY_EVALUATION = {
  financial_exposure: "",
  // Only meaningful while `financial_exposure === "custom"`.
  financial_exposure_custom_label: "",
  evaluation_date: "",
  asset_value: "",
  currency: "",
  is_liquid: false,
  has_associated_debt: false,
  // Only meaningful while `has_associated_debt` is true.
  debt_type: "",
  debt_amount: "",
  debt_currency: "",
  debt_annual_interest: "",
  debt_payoff_date: "",
  // "New Field" blocks added via "Add New Data Entry".
  data_entries: [],
};

/*
  The two series are supplied by the parent from the saved asset
  (`asset_evaluation_records` / `asset_debt_records`, decrypted in
  Utils/dataEncryption.js). They default to empty rather than to mock data, so
  an asset with no evaluations shows the empty state instead of a fake line.
*/

// Series colours: assets read as gains, debts as liabilities.
/*
  The default for both record props, shared rather than written `= []` at each
  one. A default parameter is evaluated on every render, so `= []` hands the
  effects below a brand-new array each time and they re-run for nothing. One
  frozen array means an omitted prop is the SAME omitted prop next render.
*/
const NO_RECORDS = Object.freeze([]);

/*
  Whether two series are the same list, identity aside.

  The sync effects use this to decide whether there is anything to do, and that
  is what keeps them from running away. A parent is free to write
  `records={x || []}`, or to leave the prop off entirely, and either hands over
  a fresh array on every render; setting that into state would re-render, build
  another fresh array, and set it again. React stops at fifty and reports
  "Maximum update depth exceeded" -- which is what the Add Asset modal did on
  open, once anything at all caused a second render.

  Element identity is enough: records are replaced wholesale, never mutated.
*/
const sameSeries = (a, b) =>
  a === b ||
  (a.length === b.length && a.every((record, index) => record === b[index]));

const ASSET_SERIES_COLOR = "#00A329";
const DEBT_SERIES_COLOR = "#E5352B";

// Copy fallbacks — keeps the section rendering if a language file is missing a key.
const FALLBACK = {
  assetEvaluationText1: "Asset Evaluation",
  assetEvaluationText2:
    "Record what this asset is worth over time, so your beneficiary inherits an up-to-date valuation.",
  assetEvaluationText3: "Click here to add your assets evaluation",
  assetEvaluationText4: "Hide",
  assetEvaluationText5: "Financial Exposure",
  assetEvaluationText6: "Date",
  assetEvaluationText7: "Asset Value",
  assetEvaluationText8: "Currency",
  assetEvaluationText9: "Is the asset liquid?",
  assetEvaluationText10: "This Asset Has an Associated Debt?",
  assetEvaluationText11: "Asset Evaluation Over Time",
  assetEvaluationText12: "(see records insertion history)",
  assetEvaluationText13: "Portfolio Value",
  assetEvaluationText14: "over Time",
  assetEvaluationText15: "No evaluation records yet",
  assetEvaluationText16: "Select",
  assetEvaluationText17: "No",
  assetEvaluationText18: "Yes",
  assetEvaluationText19: "Crypto",
  assetEvaluationText20: "Real Estate",
  assetEvaluationText21: "Stocks",
  assetEvaluationText22: "Bonds",
  assetEvaluationText23: "Private Equity",
  assetEvaluationText24: "Custom",
  assetEvaluationText25: "New field",
  assetEvaluationText26: "Enter a name",
  assetEvaluationText27: "Type of Debt",
  assetEvaluationText28: "Debt Amount",
  assetEvaluationText29: "Annual Interest (%)",
  assetEvaluationText30: "Payoff Date",
  assetEvaluationText31: "Asset Evaluation Input History",
  assetEvaluationText32: "Index",
  assetEvaluationText33: "Delete record",
  assetEvaluationText34: "Add New Data Entry",
  assetEvaluationText35: "New Field",
  assetEvaluationText36: "Other owners of this asset",
  assetEvaluationText37: "My Ownership (%)",
  assetEvaluationText38: "Owner n.",
  assetEvaluationText39: "Ownership (%)",
  assetEvaluationText40: "Add More Owners",
  assetEvaluationText41: "Error: Total ownership percentage should reach 100%",
  assetEvaluationText42: "Remove",
  assetEvaluationText43: "Owner",
  assetEvaluationText44: "One,Two,Three,Four,Five,Six,Seven,Eight,Nine,Ten",
  assetEvaluationText45: "Error: Please select an owner for every row",
  assetEvaluationText46: "Custom field",
  assetEvaluationText47: "Field Title",
  assetEvaluationText48: "Field Value",
  assetEvaluationText49: "Insert custom field title",
  assetEvaluationText78: "Table and\ngraph currency:",
  assetEvaluationText50: "Insert custom field value",
  assetEvaluationText51: "Add More Custom Fields",
  assetEvaluationText52: "Error: Please fill in all the fields shown",
  assetEvaluationText53: "Insert type of debt",
  assetEvaluationText54: "Debt Input History",
  assetEvaluationText55: "Debt Value",
  assetEvaluationText56: "No debt records yet",
  assetEvaluationText57: "Asset-Linked Debts",
  assetEvaluationText58: "General / Standalone Liabilities",
  assetEvaluationText59: "Primary Residence Mortgage",
  assetEvaluationText60: "Investment Property Mortgage",
  assetEvaluationText61: "Commercial Mortgage",
  assetEvaluationText62: "Auto / Vehicle Loan",
  assetEvaluationText63: "Margin / Portfolio Lombard Loan",
  assetEvaluationText64: "Commercial / Business Asset Loan",
  assetEvaluationText65: "Personal Loan",
  assetEvaluationText66: "Credit Card Debt",
  assetEvaluationText67: "Overdraft",
  assetEvaluationText68: "Student Loan",
  assetEvaluationText69: "Tax Liability",
  assetEvaluationText70: "Private / Family Loan",
  assetEvaluationText71: "General Business Debt",
  assetEvaluationText72: "Other",
  assetEvaluationText73: "Error: An evaluation already exists for this date",
  assetEvaluationText74: "Error: A debt already exists for this payoff date",
  assetEvaluationText76: "Error: Two custom fields cannot have the same title",
  assetEvaluationText77: "(no longer available)",
  assetEvaluationText86: "Review",
};

/*
  A record's date, in the reader's own order: MM/DD/YYYY in English,
  DD/MM/YYYY in French. Shared with the chart's ticks directly below the
  table, and with the Asset Management column, so the three agree.

  It used to split on "-" and print "01.06.2024". Records are stored
  YYYY/MM/DD, so the split found one part and the raw stored date went on
  screen -- which is the "2019/07/14" in the history table.
*/
const formatRecordDate = (value, langLabel) =>
  formatStoredDate(value, langLabel);

/*
  650000 -> "$650K" / "665K €", the same compact form the chart prints on its
  points. The history tables sit directly under that chart and show the same
  numbers, so a full-precision "$28,129.114" — which is what a converted
  amount looks like once the exchange rate is applied — read as a different
  figure from the "$28.1K" on the point above it.

  Placement follows the currency, not a fixed rule; see formatCompactMoney in
  Utils/assetEvaluationUtils.js.
*/
const formatMoney = (value, currencyCode) =>
  formatCompactMoney(value, currencyCode, 1) || "";

// Accepts an optional leading "-", digits only; keeps the raw value in state.
function parseAmount(raw) {
  const cleaned = String(raw).replace(/[^\d-]/g, "");
  const negative = cleaned.startsWith("-");
  const digits = cleaned.replace(/-/g, "");
  if (digits === "") return negative ? "-" : "";
  return `${negative ? "-" : ""}${digits}`;
}

/*
  A debt is always a liability, so its amount is pinned negative: the user types
  digits and the minus sign is not theirs to remove. Existing positive values
  coming back from the API are normalised the same way on the way in.
*/
function parseDebtAmount(raw) {
  const digits = String(raw).replace(/\D/g, "");
  return digits === "" ? "" : `-${digits}`;
}

// Digits plus at most one decimal point; never negative.
function parsePercent(raw) {
  const cleaned = String(raw).replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  return parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : parts[0];
}

// "1" -> "1%" — the design renders the unit inline, right after the number.
function displayPercent(raw) {
  return raw === "" ? "" : `${raw}%`;
}

/*
  Exposes an imperative `validate()` so the modal's Save can gate on it:
  returns false when a data entry's ownership does not total 100%, and in that
  case reveals the section and scrolls it into view.
*/
// Stable no-ops: a host that asks no review questions changes nothing here.
/*
  No host to ask about proposals -- Add Asset, where nothing can have been
  proposed yet because the asset does not exist.

  It returns the CHILDREN, not null. Every caller hands this its own label as
  children and lets it decide whether to pair it with a review switch, which
  is the contract ProposedField states: "it returns that label untouched when
  there is nothing to ask". A default that answered null dropped the label
  instead -- so in Add Asset the two switch rows drew their toggles with no
  words beside them, and the debt fields lost their captions the same way.
*/
const NO_PROPOSAL = (field, label, children = null) => children;
const NO_MARK = () => "";
const NO_FIELDS = Object.freeze([]);

/*
  The fields this panel draws, so it can tell whether a pending question is
  one of its own. A suggested asset name is not a reason to open the
  evaluation section.
*/
const PANEL_FIELDS = new Set([
  "financial_exposure",
  "financial_exposure_custom_label",
  "evaluation_date",
  "asset_value",
  "currency",
  "is_liquid",
  "has_associated_debt",
  "debt_type",
  "debt_amount",
  "debt_currency",
  "debt_annual_interest",
  "debt_payoff_date",
  "asset_evaluation_records",
  "asset_debt_records",
]);

// Shared empty set, so the default prop is stable between renders.
const NO_KEYS = new Set();

const AssetEvaluation = React.forwardRef(function AssetEvaluation(
  {
    value,
    onChange,
    records = NO_RECORDS,
    /*
      Readings a partner has proposed and the customer has not answered yet.

      Shown in the history table under the real ones, marked, because that is
      where a reader looks for "what readings does this asset have" -- and a
      suggested one is part of that question even before it is accepted. They
      are NOT in `records`: nothing here is in the asset until the customer
      saves it, so they are counted in no total, drawn on no chart, and
      cannot be deleted from a list they are not in yet.
    */
    proposedRecords = NO_RECORDS,
    proposedDebtRecords = NO_RECORDS,
    /*
      And which rows of `records` are an accepted suggestion.

      Accepting one moves it into the asset's own list, where it looked
      exactly like a reading the customer had taken themselves -- so the
      customer reviewing a proposed valuation saw the mark disappear the
      moment they chose to keep it, which reads as "there is nothing here
      to review" at precisely the wrong moment. Marked in place instead.
    */
    proposedRecordKeys = NO_KEYS,
    proposedDebtRecordKeys = NO_KEYS,
    /*
      The same question as those records, asked about the scalar fields here:
      a switch above each field a partner has proposed a change to, and the
      purple outline on the field itself.

      Supplied by whoever is hosting this panel rather than worked out here,
      because the proposals belong to the asset, not to the evaluation -- Edit
      Asset holds them, decrypts them and decides what accepting one does.
      Both default to nothing, so the customer's own panel and the Add Asset
      modal render exactly as they did.
    */
    renderProposal = NO_PROPOSAL,
    proposalMark = NO_MARK,
    /*
      Which fields have a question waiting, by name. Names only -- the values
      stay with the host, which is the one that knows what accepting one
      does. Used here for one thing: deciding what to open.
    */
    proposedFields = NO_FIELDS,
    disabled = false,
    // Optional hook fired alongside opening the history section.
    onOpenHistory,
    // "Add New Data Entry" — no behaviour agreed yet; fired if supplied.
    onAddEntry,
    /*
      Called when a history row's delete is clicked, as (record, index). When
      provided the parent owns the list; otherwise the row is removed locally
      so the mockup stays interactive. Wire to the delete endpoint — see spec.
    */
    onDeleteRecord,
    /*
      The debt series — same shape as `records`, values always negative. Plotted
      in red alongside the asset series and listed in "Debt Input History".
    */
    debtRecords = NO_RECORDS,
    // Same contract as `onDeleteRecord`, for a debt history row.
    onDeleteDebtRecord,
    /*
      The current user's previously saved custom exposure types, as
      [{ value, label }]. Per-user, never shared — see
      docs/asset-evaluation-backend-spec.md. Empty until the endpoint exists.
    */
    customExposureOptions = [],
    /*
      "evaluation" or "debt": expand the panel on mount and scroll to that part.
      Used when the Asset Management table sends the user here because the cell
      they clicked has no history to edit inline.
    */
    focus,
    /*
      Records the reading on its own, without saving or closing the asset —
      called with the current evaluation once its four fields check out. The
      Save button under the currency only renders when this is supplied, so a
      caller that has nowhere to put a reading does not offer to take one.

      What "saved" means is the caller's to decide, and the two differ: the Edit
      Asset modal writes it to the database there and then, while Add Asset can
      only hold it — there is no asset yet to hang a record on.
    */
    onSaveReading,
    /*
      The debt block's own Save. Supplied only where a debt can actually be
      written -- Edit Asset -- so Add Asset, which has no asset to hang one
      on yet, simply does not render the button.
    */
    onSaveDebt,
    debtCommitted,
    /*
      A "New Field" block's own Save, one per block. Supplied only where a
      field can actually be written -- Edit Asset -- so Add Asset, which has
      no asset to hang one on yet, does not render the buttons.
    */
    onSaveEntry,
  },
  ref
) {
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.EditAsset?.AssetEvaluation || FALLBACK
  );
  useEffect(() => {
    setContent(websiteCurrentLang?.content?.User?.EditAsset?.AssetEvaluation || FALLBACK);
  }, [websiteCurrentLang]);

  const t = (key) => content?.[key] || FALLBACK[key];

  // EUR in French, USD in English — follows the language, like the € / $ symbol.
  const defaultCurrency = defaultCurrencyFor(websiteCurrentLang?.label);

  const sectionRef = useRef(null);
  const debtRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [debtHistoryExpanded, setDebtHistoryExpanded] = useState(false);
  const [internal, setInternal] = useState(value || EMPTY_EVALUATION);

  /*
    An asset that already carries evaluation data opens with the panel showing.

    Collapsed is the right start for a NEW asset, where the section is optional
    and an empty panel is just noise. For one that has been evaluated already,
    the opposite holds: the figures are there and hiding them behind a "Show"
    makes the modal look as though the asset has none.

    Anything the panel would actually display counts — a recorded valuation or
    debt, an exposure, a debt description, or an owners/custom-field block. The
    reading fields are excluded on purpose: they reopen blank by design, so they
    say nothing about whether the asset has been evaluated.

    Runs once. The data arrives with the modal but not always on the first
    render, hence an effect rather than the initial state; the ref is what stops
    it reopening the panel after the user has deliberately collapsed it.
  */
  const hasEvaluationData =
    (records || []).length > 0 ||
    (debtRecords || []).length > 0 ||
    Boolean(value?.financial_exposure) ||
    value?.has_associated_debt === true ||
    (value?.data_entries || []).length > 0;
  const autoExpanded = useRef(false);
  useEffect(() => {
    if (autoExpanded.current || !hasEvaluationData) return;
    autoExpanded.current = true;
    setExpanded(true);
  }, [hasEvaluationData]);

  /*
    A question the reader cannot reach is a question they cannot answer.

    This panel and its two history sections all start closed, so a partner's
    suggestion about a valuation, a debt or either history would sit behind a
    "Show" with nothing on screen saying it was there -- and the customer
    would save without ever being asked.

    Opened once, when the modal arrives carrying one. The ref is what stops it
    reopening a section the reader has deliberately collapsed, the same way
    the auto-expand above works.
  */
  const reviewOpened = useRef(false);
  useEffect(() => {
    if (reviewOpened.current) return;
    const mine = (proposedFields || []).filter((f) => PANEL_FIELDS.has(f));
    if (!mine.length) return;
    reviewOpened.current = true;
    setExpanded(true);
    if (mine.includes("asset_evaluation_records")) setHistoryExpanded(true);
    if (mine.includes("asset_debt_records")) setDebtHistoryExpanded(true);
  }, [proposedFields]);

  /*
    Arriving with a focus target: reveal the panel, turn the debt switch on when
    that is what they came to fill in, and scroll it into view. Runs once —
    re-running would yank the page back while they are typing.
  */
  const focusApplied = useRef(false);
  useEffect(() => {
    if (!focus || focusApplied.current) return;
    focusApplied.current = true;
    setExpanded(true);
    if (focus === "debt") update({ has_associated_debt: true });
    // After the panel has painted, or there is nothing to scroll to yet.
    window.requestAnimationFrame(() => {
      const target =
        (focus === "debt" && debtRef.current) || sectionRef.current;
      if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  /*
    Records are controlled by the parent when `onDeleteRecord` is supplied;
    otherwise kept locally so deleting a row visibly updates both the table and
    the chart while the endpoints are pending.
  */
  const [internalRecords, setInternalRecords] = useState(records);
  useEffect(() => {
    // Returning `prev` unchanged is what makes React bail out -- see sameSeries.
    setInternalRecords((prev) => (sameSeries(prev, records) ? prev : records));
  }, [records]);
  const activeRecords = onDeleteRecord ? records : internalRecords;

  /*
    The row index is the position in the SORTED table, which is no longer the
    position in the stored array, so the fallback below matches on the record's
    id instead — filtering by index would delete whichever reading happened to
    sit at that spot. The parent's handler already matches by id.
  */
  const handleDeleteRecord = (record, index) => {
    if (disabled) return;
    if (onDeleteRecord) {
      onDeleteRecord(record, index);
      return;
    }
    setInternalRecords((prev) =>
      record?.id
        ? prev.filter((entry) => entry.id !== record.id)
        : prev.filter((_, i) => i !== index)
    );
  };

  // The debt series, held on exactly the same terms as the asset one above.
  const [internalDebtRecords, setInternalDebtRecords] = useState(debtRecords);
  useEffect(() => {
    setInternalDebtRecords((prev) =>
      sameSeries(prev, debtRecords) ? prev : debtRecords
    );
  }, [debtRecords]);
  const activeDebtRecords = onDeleteDebtRecord ? debtRecords : internalDebtRecords;

  // Matched by id for the same reason as the asset series above.
  const handleDeleteDebtRecord = (record, index) => {
    if (disabled) return;
    if (onDeleteDebtRecord) {
      onDeleteDebtRecord(record, index);
      return;
    }
    setInternalDebtRecords((prev) =>
      record?.id
        ? prev.filter((entry) => entry.id !== record.id)
        : prev.filter((_, i) => i !== index)
    );
  };

  /*
    The asset and debt histories are the same table with a different series and
    a different value-column heading, so one renderer serves both.
  */
  const renderHistoryTable = ({
    records,
    valueHeader,
    emptyText,
    onDelete,
    // Suggested by the partner, not yet part of the asset.
    proposed = NO_RECORDS,
    // Rows of `records` that ARE the suggestion, accepted but not yet saved.
    markKeys = NO_KEYS,
  }) => (
    <div className={styles.history_card}>
      {records.length === 0 && proposed.length === 0 ? (
        <div className={styles.history_empty}>{emptyText}</div>
      ) : (
        <div className={styles.history_scroll}>
          <table className={styles.history_table}>
            <thead>
              <tr>
                <th className={styles.col_index}>{t("assetEvaluationText32")}</th>
                <th className={styles.col_date}>{t("assetEvaluationText6")}</th>
                <th className={styles.col_value}>{valueHeader}</th>
                <th className={styles.col_action} aria-label="" />
              </tr>
            </thead>
            <tbody>
              {records.map((record, index) => {
                // id, or the date for records older than ids -- see proposedKeys.
                const key = String(record?.id ?? record?.date);
                const suggested = markKeys.has(key);
                return (
                <tr
                  key={`${record.date}-${index}`}
                  className={suggested ? styles.history_row_proposed : undefined}
                >
                  <td className={`${styles.col_index} ${styles.cell_index}`}>
                    {index + 1}
                  </td>
                  <td
                    className={`${styles.col_date} ${
                      suggested ? styles.cell_date_proposed : ""
                    }`}
                  >
                    {formatRecordDate(record.date, websiteCurrentLang?.label)}
                    {suggested && (
                      <span className={styles.history_review}>
                        <span aria-hidden="true">!</span>
                        {t("assetEvaluationText86")}
                      </span>
                    )}
                  </td>
                  <td className={styles.col_value}>
                    {formatMoney(record.value, displayCurrency)}
                  </td>
                  <td className={styles.col_action}>
                    <button
                      type="button"
                      className={styles.row_delete}
                      title={t("assetEvaluationText33")}
                      aria-label={t("assetEvaluationText33")}
                      disabled={disabled}
                      onClick={() => onDelete(record, index)}
                    />
                  </td>
                </tr>
                );
              })}

              {/*
                The suggested readings, under the asset's own.

                Marked rather than mixed in: purple is what this product uses
                wherever something is waiting for the customer, and the row
                carries the same "Review" word their asset row does. No delete
                -- there is nothing here to delete yet. Accepting the change
                is what turns one of these into a row above.
              */}
              {proposed.map((record, index) => (
                <tr
                  className={styles.history_row_proposed}
                  key={`proposed-${record.date}-${index}`}
                >
                  <td className={`${styles.col_index} ${styles.cell_index}`}>
                    {records.length + index + 1}
                  </td>
                  <td className={`${styles.col_date} ${styles.cell_date_proposed}`}>
                    {formatRecordDate(record.date, websiteCurrentLang?.label)}
                    <span className={styles.history_review}>
                      <span aria-hidden="true">!</span>
                      {t("assetEvaluationText86")}
                    </span>
                  </td>
                  <td className={styles.col_value}>
                    {formatMoney(record.value, displayCurrency)}
                  </td>
                  <td className={styles.col_action} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Controlled when the parent passes `value`, uncontrolled otherwise.
  const evaluation = value || internal;

  const update = (patch) => {
    const next = { ...evaluation, ...patch };
    if (!value) setInternal(next);
    if (onChange) onChange(next);
  };

  /*
    A debt that arrives from the API — or was saved before the field pinned its
    sign — may still be positive. Normalise it once so the value on screen and
    the value that would be saved agree; the field itself can no longer produce
    a positive number.
  */
  useEffect(() => {
    const current = evaluation.debt_amount;
    if (current === "" || current === null || current === undefined) return;
    const normalised = parseDebtAmount(current);
    if (normalised !== String(current)) update({ debt_amount: normalised });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation.debt_amount]);

  const isCustomExposure =
    evaluation.financial_exposure === FINANCIAL_EXPOSURE_CUSTOM;

  /*
    Percent fields carry the "%" inside their value, so after every keystroke
    the caret would land behind it. Pin it back in front so typing keeps
    appending to the number. Shared by the interest and ownership inputs.
  */
  const handlePercentChange = (event, apply) => {
    const input = event.currentTarget;
    const digits = parsePercent(input.value);
    apply(digits);
    window.requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.setSelectionRange(digits.length, digits.length);
      }
    });
  };

  /*
    Owner options: this user's beneficiaries, plus their own assets of type
    "Company Shares", plus a "Custom" sentinel. Values are namespaced so the
    backend can tell the two sources apart.
  */
  const userData = UserData();
  /*
    Owners are stored as plain NAMES, not as references.

    A deliberate trade: renaming a beneficiary or a company asset will not
    update the ownership rows that already name them. Accepted, because the
    alternative — storing `beneficiary:<id>` and resolving the label at render —
    means a deleted beneficiary leaves an unresolvable row, and the owner list
    is a record of who held what at the time rather than a live link.

    All three company-shaped asset types qualify, each stored under its own
    value: "Company", "Company Shares" and "Business Ownership".
  */
  const COMPANY_TYPES = ["company", "company shares", "business ownership"];
  const ownerOptions = useMemo(() => {
    const root = (userData && userData.UserData) || {};
    const beneficiaries = (root.beneficiaries || []).map((person) =>
      [person.name, person.last_name].filter(Boolean).join(" ").trim()
    );
    const companies = (root.assets || [])
      .filter((asset) =>
        COMPANY_TYPES.includes(
          String(asset?.asset_type || "").trim().toLowerCase()
        )
      )
      .map((asset) => String(asset.asset_name || "").trim());
    /*
      And the owners this user has typed in before. Saved per user, so a name
      entered on one asset is offered on the next instead of being retyped.
    */
    const own = (root.asset_owner_custom_options || []).map((option) =>
      String(option?.label || "").trim()
    );
    // Deduped and blanks dropped: two people with the same name would otherwise
    // produce two identical options, only one of which could ever be picked.
    return Array.from(
      new Set([...beneficiaries, ...companies, ...own])
    ).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  /*
    Rows saved before this change hold "beneficiary:<id>" / "asset:<id>".
    Resolved to the current name for display so they do not show as raw ids;
    the next save rewrites them as names.
  */
  const legacyName = (stored) => {
    const root = (userData && userData.UserData) || {};
    if (typeof stored !== "string") return stored;
    if (stored.startsWith("beneficiary:")) {
      const id = stored.slice("beneficiary:".length);
      const person = (root.beneficiaries || []).find((b) => b.user_id === id);
      return person
        ? [person.name, person.last_name].filter(Boolean).join(" ").trim()
        : stored;
    }
    if (stored.startsWith("asset:")) {
      const id = stored.slice("asset:".length);
      const found = (root.assets || []).find(
        (a) => (a.id != null ? a.id : a.asset_id) === id
      );
      return found ? String(found.asset_name || "").trim() || stored : stored;
    }
    return stored;
  };

  const ownerWords = String(t("assetEvaluationText44") || "").split(",");
  const ownerPlaceholder = (i) =>
    `${t("assetEvaluationText43")} ${ownerWords[i] || i + 1}`;

  // ---- "New Field" data-entry blocks ----
  const dataEntries = evaluation.data_entries || [];
  // Both keyed by id, populated only by `validate()` on a save attempt.
  const [ownershipErrors, setOwnershipErrors] = useState({});
  const [ownerErrors, setOwnerErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Marks a required-but-empty field, and clears its flag once filled.
  const invalidClass = (key) => (fieldErrors[key] ? styles.field_invalid : "");
  const clearFieldError = (key, value) => {
    if (fieldErrors[key] && String(value || "").trim() !== "") {
      setFieldErrors((prev) => ({ ...prev, [key]: false }));
    }
  };

  const setDataEntries = (next) => update({ data_entries: next });

  /*
    Applies the new list and, if this entry was already flagged, clears the
    flag as soon as it totals 100% — so a shown error disappears while the
    user fixes it, without validating anything that has not been flagged yet.
  */
  const applyEntries = (next, touchedEntryId) => {
    setDataEntries(next);
    if (!touchedEntryId || !ownershipErrors[touchedEntryId]) return;
    const entry = next.find((e) => e.id === touchedEntryId);
    if (entry && ownershipTotal(entry) === TOTAL_OWNERSHIP) {
      setOwnershipErrors((prev) => ({ ...prev, [touchedEntryId]: false }));
    }
  };

  const patchEntry = (entryId, patch) =>
    applyEntries(
      dataEntries.map((e) => (e.id === entryId ? { ...e, ...patch } : e)),
      entryId
    );

  /*
    Switching a block's type must leave it with something to fill in.

    Each block only persists the set matching its type, so an entry loaded from
    a saved asset — or one just switched — has an empty list for the other side.
    Rendering that as nothing looks like the form has vanished and forces the
    user to hunt for "Add More…" before they can type. So the target set is
    seeded with one blank row whenever it is empty; whatever the other side held
    stays in local state, so flipping back and forth loses nothing until save.
  */
  const changeEntryType = (entryId, nextType) => {
    const entry = dataEntries.find((e) => e.id === entryId);
    const patch = { type: nextType };
    if (nextType === "other_owners" && !(entry?.owners || []).length) {
      patch.owners = [makeOwner()];
    }
    if (nextType === "custom" && !(entry?.custom_fields || []).length) {
      patch.custom_fields = [makeCustomField()];
    }
    patchEntry(entryId, patch);
  };

  const patchOwner = (entryId, ownerId, patch) =>
    applyEntries(
      dataEntries.map((e) =>
        e.id === entryId
          ? {
              ...e,
              owners: e.owners.map((o) =>
                o.id === ownerId ? { ...o, ...patch } : o
              ),
            }
          : e
      ),
      entryId
    );

  /*
    A new block opens on the first type the asset does not already use.

    Opening on "Other owners" unconditionally offered a second one of a block
    the asset can only have one of — the option was greyed out in the dropdown
    while being the very thing the field was set to, so the only way forward was
    to change it. "Custom" is repeatable and last in the list, so there is
    always something left to land on.
  */
  const addDataEntry = () => {
    const taken = takenEntryTypes(dataEntries);
    const next = DATA_ENTRY_TYPES.find((option) => !taken.has(option.value));
    setDataEntries([...dataEntries, makeDataEntry(next && next.value)]);
    if (onAddEntry) onAddEntry();
  };

  const removeDataEntry = (entryId) => {
    setDataEntries(dataEntries.filter((e) => e.id !== entryId));
    setOwnershipErrors((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
  };

  const addOwner = (entryId) => {
    const entry = dataEntries.find((e) => e.id === entryId);
    if (!entry || entry.owners.length >= MAX_OWNERS) return;
    patchEntry(entryId, { owners: [...entry.owners, makeOwner()] });
  };

  // Never removes the last owner — one is always required.
  const removeOwner = (entryId, ownerId) => {
    const entry = dataEntries.find((e) => e.id === entryId);
    if (!entry || entry.owners.length <= 1) return;
    patchEntry(entryId, {
      owners: entry.owners.filter((o) => o.id !== ownerId),
    });
  };

  const patchCustomField = (entryId, fieldId, patch) =>
    applyEntries(
      dataEntries.map((e) =>
        e.id === entryId
          ? {
              ...e,
              custom_fields: (e.custom_fields || []).map((f) =>
                f.id === fieldId ? { ...f, ...patch } : f
              ),
            }
          : e
      ),
      entryId
    );

  const addCustomField = (entryId) => {
    const entry = dataEntries.find((e) => e.id === entryId);
    if (!entry) return;
    patchEntry(entryId, {
      custom_fields: [...(entry.custom_fields || []), makeCustomField()],
    });
  };

  // Never removes the last one — one custom field is always required.
  const removeCustomField = (entryId, fieldId) => {
    const entry = dataEntries.find((e) => e.id === entryId);
    if (!entry || (entry.custom_fields || []).length <= 1) return;
    patchEntry(entryId, {
      custom_fields: entry.custom_fields.filter((f) => f.id !== fieldId),
    });
  };

  const ownershipTotal = (entry) => {
    const sum = [
      entry.my_ownership,
      ...entry.owners.map((o) => o.ownership),
    ].reduce((acc, v) => acc + (v === "" || v === "." ? 0 : Number(v)), 0);
    return Math.round(sum * 100) / 100;
  };

  /*
    Called by the modal's Save via the imperative handle.

    Everything currently on screen has to be filled in: the panel's own fields,
    the debt block while its switch is on, and each data entry's fields for
    whichever type it is set to. On top of that, an "other owners" entry's
    percentages must total 100%.

    The panel's own fields are only required once the section has actually been
    used — otherwise simply expanding it out of curiosity would block saving an
    otherwise valid asset.
  */
  const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";

/*
    The panel holds independent parts, and each is validated on its own: fill
    one field in a part and you must finish that part, but the others stay out
    of it. So a user adding only a debt is not asked to complete a valuation,
    and vice versa.

    Within the evaluation part, the financial exposure stands apart from the
    reading. Exposure describes the ASSET and there is only one per asset, so it
    can be set on its own. A reading — date, value, currency — is a measurement,
    and cannot be recorded without saying what is being measured, so entering
    any of it demands an exposure too.

    `currency` is excluded from the test because the effect below writes the
    language default into it; counting it would make a reading look started the
    moment anything else on the panel was touched.
  */
  const readingEngaged = () =>
    [evaluation.evaluation_date, evaluation.asset_value].some(
      (v) => !isBlank(v)
    );


  /*
    The pickers display `defaultCurrency` from the start, but it is only written
    into the value once the section is genuinely in use — the asset's own
    currency as soon as anything in the panel is filled, the debt's as soon as
    the debt switch is on. Writing it any earlier would make the evaluation part
    read as "in use" for every asset and demand the rest of it be completed on an
    asset the user never opened the section for.
  */
  useEffect(() => {
    const patch = {};
    if (readingEngaged() && isBlank(evaluation.currency)) {
      patch.currency = defaultCurrency;
    }
    if (
      evaluation.has_associated_debt &&
      !isBlank(evaluation.debt_amount) &&
      isBlank(evaluation.debt_currency)
    ) {
      patch.debt_currency = defaultCurrency;
    }
    /*
      Income blocks have exactly the gap described below for debt_currency, and
      it is worse here: their select shows `defaultCurrency` from the moment the
      block becomes an income, so the currency LOOKS chosen while the payload
      still holds "". validate() then refuses to save, pointing at a field that
      is plainly filled in on screen — and because a data-entry block is only
      stored when the save goes through, the block silently never persisted.

      No "engaged" test to make here: an income block exists because the user
      picked that type, and validate() demands both halves of it either way.
    */
    const entries = evaluation.data_entries || [];
    if (
      entries.some(
        (entry) =>
          INCOME_ENTRY_TYPES.includes(entry?.type) && isBlank(entry?.currency)
      )
    ) {
      patch.data_entries = entries.map((entry) =>
        INCOME_ENTRY_TYPES.includes(entry?.type) && isBlank(entry?.currency)
          ? { ...entry, currency: defaultCurrency }
          : entry
      );
    }

    if (Object.keys(patch).length > 0) update(patch);
    /*
      Every value the body reads has to be listed, or the default is never
      written for it.

      `debt_amount` was missing, which is exactly the state the picker cannot
      show: the debt currency select displays `defaultCurrency` when it holds
      nothing, so typing an amount looked like it had a currency while the
      payload still had "" — and Save asked for a currency that was already on
      screen. Touching the dropdown wrote a real value and the complaint went
      away, which is why it looked like the select was at fault.

      `asset_value` and `evaluation_date` were both already here, so the asset's
      own currency never had the same gap.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    evaluation.financial_exposure,
    evaluation.evaluation_date,
    evaluation.asset_value,
    evaluation.currency,
    evaluation.debt_amount,
    evaluation.debt_currency,
    evaluation.has_associated_debt,
    evaluation.data_entries,
    defaultCurrency,
  ]);

  /*
    What the reading owes, expressed once and asked twice: by validate() below
    for the asset's own Save, and by saveReading() for the Save under the
    currency. Two copies would drift, and the drift would show up as a reading
    the small button accepts and the big one then rejects.

    `demandReading` is the difference between them. For the asset's Save a
    reading is optional — an asset may be saved with none — so the fields are
    only demanded once one of them is filled. Pressing the reading's own Save
    IS the request to record one, so there they are demanded outright.
  */
  const requireReadingFields = (require, { demandReading = false } = {}) => {
    /*
      Exposure on its own is fine — it describes the asset, not a reading. The
      only thing it owes is a name when "Custom" is picked.
    */
    if (evaluation.financial_exposure === FINANCIAL_EXPOSURE_CUSTOM) {
      require("main:custom_label", evaluation.financial_exposure_custom_label);
    }

    /*
      Backstop. The dropdown stops offering "Select" once a reading exists, so
      this should be unreachable from the UI — it guards against a blank
      arriving some other way rather than being the thing the user runs into.

      Debt history counts the same way: an asset carrying either kind of record
      has to say what it is.
    */
    if (
      (activeRecords || []).length > 0 ||
      (activeDebtRecords || []).length > 0
    ) {
      require("main:financial_exposure", evaluation.financial_exposure);
    }

    /*
      A reading is all-or-nothing, and cannot exist without an exposure: a value
      on a date means nothing without saying what was valued.

      A date that already has a record is not an error: saving it replaces that
      record (see upsertRecord in Utils/assetEvaluationUtils.js), so the chart
      keeps one point per date and a correction overwrites rather than stacking.
    */
    if (demandReading || readingEngaged()) {
      require("main:financial_exposure", evaluation.financial_exposure);
      require("main:evaluation_date", evaluation.evaluation_date);
      require("main:asset_value", evaluation.asset_value);
      require("main:currency", evaluation.currency);
    }
  };

  /*
    The Save under the currency: record this reading, leave everything else
    alone, and keep the modal open.

    Only the reading's own flags are touched — merged into whatever is already
    showing rather than replacing it — because this button did not look at the
    debt block or the ownership rows and must not clear or raise their errors.
  */
  const saveReading = () => {
    if (disabled || !onSaveReading) return;
    const fieldFlags = {};
    let valid = true;
    requireReadingFields(
      (key, value) => {
        const missing = isBlank(value);
        fieldFlags[key] = missing;
        if (missing) valid = false;
      },
      { demandReading: true }
    );
    setFieldErrors((prev) => ({ ...prev, ...fieldFlags }));
    if (!valid) return;
    onSaveReading(evaluation);
  };

  /*
    The Save inside the debt block.

    Checks the debt and nothing else -- the reading above it is a separate
    claim with its own button, and neither should be able to block the other.
    missingDebtFields decides what a debt needs, the same helper the asset's
    own save and the Asset Management debt modal use, so all three agree.

    `amountRequired` because the toggle IS the claim that a debt exists:
    pressing Save with it on and nothing filled has to ask for the figures.
  */
  const saveDebt = () => {
    if (disabled || !onSaveDebt) return;
    const flags = {};
    let valid = true;
    if (evaluation.has_associated_debt) {
      const missing = missingDebtFields(evaluation, { amountRequired: true });
      for (const [field, key] of [
        ["debt_type", "debt:type"],
        ["debt_amount", "debt:amount"],
        ["debt_currency", "debt:currency"],
      ]) {
        const bad = missing.has(field);
        flags[key] = bad;
        if (bad) valid = false;
      }
    }
    setFieldErrors((prev) => ({ ...prev, ...flags }));
    if (!valid) return;
    onSaveDebt(evaluation);
  };

  /*
    One button, rendered in two places -- see each site for why.
  */
  const debtSaveButton = (
    <button
      type="button"
      className={styles.reading_save}
      disabled={disabled}
      onClick={saveDebt}
    >
      {t("assetEvaluationText85")}
    </button>
  );

  /*
    One "New Field" block's own checks, lifted out of validate() so the block's
    Save can ask for exactly the same thing the asset's Save does -- one list,
    so the two can never drift into disagreeing about what a field needs.

    Writes into the caller's `flags` / `ownerFlags` and reports through
    `require`, the way it did inline.
  */
  const requireEntryFields = (entry, require, flags, ownerFlags, fail) => {
    require(`entry:${entry.id}:type`, entry.type);
    if (entry.type === "other_owners") {
      const invalid = ownershipTotal(entry) !== TOTAL_OWNERSHIP;
      flags[entry.id] = invalid;
      if (invalid) fail();
      require(`entry:${entry.id}:my_ownership`, entry.my_ownership);
      entry.owners.forEach((owner) => {
        /*
          "Custom" on its own does not name anyone — it is the prompt for the
          field that replaces the dropdown, so that field has to be filled in
          before the row counts as complete.
        */
        const missing =
          isBlank(owner.owner) ||
          (owner.owner === OWNER_CUSTOM && isBlank(owner.owner_custom_label));
        ownerFlags[owner.id] = missing;
        if (missing) fail();
        require(`owner:${owner.id}:ownership`, owner.ownership);
      });
    } else if (entry.type === "location") {
      // A location entry with no country says nothing at all.
      require(`entry:${entry.id}:country`, entry.country);
    } else if (INCOME_ENTRY_TYPES.includes(entry.type)) {
      // An income is a figure in a currency; neither half stands alone.
      require(`entry:${entry.id}:amount`, entry.amount);
      require(`entry:${entry.id}:currency`, entry.currency);
    } else if (entry.type === "custom") {
      (entry.custom_fields || []).forEach((field) => {
        require(`cf:${field.id}:title`, field.title);
        require(`cf:${field.id}:value`, field.value);
      });
    }
  };

  /*
    The Save inside one "New Field" block.

    Checks that block and nothing else, through the same requireEntryFields the
    asset's own Save uses, so a field cannot be accepted here and refused
    there. Custom-field titles are the one rule it cannot check alone -- they
    have to be unique across the whole asset -- so that check is repeated here
    against the other blocks.
  */
  const saveEntry = (entryId) => {
    if (disabled || !onSaveEntry) return;
    const entries = evaluation.data_entries || [];
    const entry = entries.find((item) => item.id === entryId);
    if (!entry) return;

    const fieldFlags = {};
    const flags = {};
    const ownerFlags = {};
    let valid = true;
    const require = (key, value) => {
      const missing = isBlank(value);
      fieldFlags[key] = missing;
      if (missing) valid = false;
    };
    requireEntryFields(entry, require, flags, ownerFlags, () => {
      valid = false;
    });

    /*
      A title this block shares with another block's field would collapse two
      Asset Management columns into one, so it is refused here as well.
    */
    if (entry.type === "custom") {
      const taken = new Set();
      entries.forEach((other) => {
        if (other.id === entryId || other.type !== "custom") return;
        (other.custom_fields || []).forEach((field) => {
          const key = String(field.title || "").trim().toLowerCase();
          if (key) taken.add(key);
        });
      });
      (entry.custom_fields || []).forEach((field) => {
        const key = String(field.title || "").trim().toLowerCase();
        if (key && taken.has(key)) {
          fieldFlags[`cf:${field.id}:title`] = true;
          valid = false;
        }
      });
    }

    setFieldErrors((prev) => ({ ...prev, ...fieldFlags }));
    setOwnershipErrors((prev) => ({ ...prev, ...flags }));
    setOwnerErrors((prev) => ({ ...prev, ...ownerFlags }));
    if (!valid) return;
    onSaveEntry(evaluation, entryId);
  };

  const validate = () => {
    const entries = evaluation.data_entries || [];
    const flags = {};
    const ownerFlags = {};
    const fieldFlags = {};
    let valid = true;

    const require = (key, value) => {
      const missing = isBlank(value);
      fieldFlags[key] = missing;
      if (missing) valid = false;
    };

    requireReadingFields(require);

    /*
      The debt is NOT checked here.

      It IS written here -- whatever is in the block is saved with the asset,
      pressed or not. What the asset's save does not do is demand it: ticking
      the toggle and typing nothing used to block a save that had nothing to
      do with the debt.

      The asking belongs to the block's own Save, which is the one gesture
      that is about the debt and so the one place a complaint about it makes
      sense. missingDebtFields still decides what a debt needs; saveDebt is
      where it is now asked.
    */

    entries.forEach((entry) =>
      requireEntryFields(entry, require, flags, ownerFlags, () => {
        valid = false;
      })
    );

    /*
      Custom field titles must be unique across the WHOLE asset, not just within
      one block. Each title becomes a column on the Asset Management table,
      keyed `custom:<title>`, so two fields sharing one would collapse into a
      single column and only one of the values would ever be visible.

      Compared case-insensitively on the trimmed title, the same way asset names
      and exposure labels are.
    */
    const titleCounts = {};
    entries.forEach((entry) => {
      if (entry.type !== "custom") return;
      (entry.custom_fields || []).forEach((field) => {
        const key = String(field.title || "").trim().toLowerCase();
        if (!key) return;
        titleCounts[key] = (titleCounts[key] || 0) + 1;
      });
    });
    entries.forEach((entry) => {
      if (entry.type !== "custom") return;
      (entry.custom_fields || []).forEach((field) => {
        const key = String(field.title || "").trim().toLowerCase();
        if (!key || titleCounts[key] < 2) return;
        fieldFlags[`cf:${field.id}:title`] = true;
        fieldFlags[`cf:${field.id}:duplicate`] = true;
        valid = false;
      });
    });

    setOwnershipErrors(flags);
    setOwnerErrors(ownerFlags);
    setFieldErrors(fieldFlags);
    if (!valid) {
      setExpanded(true); // the error is inside the collapsible panel
      window.requestAnimationFrame(() => {
        if (sectionRef.current) {
          sectionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      });
    }
    return valid;
  };

  useImperativeHandle(ref, () => ({ validate }));

  /*
    Currency picker is identical in the evaluation and debt rows. It never shows
    the placeholder: with no saved value it opens on the language's own currency
    (`defaultCurrency`), which the effect below then writes into the payload.
  */
  /*
    The exposure list, in the shape `Dropdown` wants.

    One group with no heading, so it renders as a flat list — the grouping in
    the debt-type list is what makes headings useful there, and there is nothing
    to group here.

    Built in the same order the native <select> listed them: the placeholder
    first and only while it is still offerable, then the built-ins, then this
    user's own types, then a stored value that has fallen out of both lists, and
    Custom last.
  */
  const exposureGroups = useMemo(() => {
    const options = [];
    /*
      Clearing is only offered while the asset has no readings. Once a value has
      been recorded the exposure is what that value is a valuation OF, so the
      empty row is simply not listed — better than offering it and then refusing
      the save.
    */
    if (
      (activeRecords || []).length === 0 &&
      (activeDebtRecords || []).length === 0
    ) {
      options.push({ value: "", label: t("assetEvaluationText16") });
    }
    FINANCIAL_EXPOSURE_OPTIONS.forEach((option) =>
      options.push({ value: option.value, label: t(option.labelKey) })
    );
    (customExposureOptions || []).forEach((option) =>
      options.push({ value: option.value, label: option.label })
    );
    /*
      A stored exposure in neither list — an id whose option has gone missing.
      Listed so the field shows what the asset actually holds rather than
      reading as empty and silently rewriting it on the next save.
    */
    const current = evaluation.financial_exposure;
    if (
      current &&
      current !== FINANCIAL_EXPOSURE_CUSTOM &&
      !options.some((option) => option.value === current)
    ) {
      options.push({ value: current, label: current });
    }
    options.push({
      value: FINANCIAL_EXPOSURE_CUSTOM,
      label: t("assetEvaluationText24"),
    });
    return [{ label: "", options }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    content,
    customExposureOptions,
    evaluation.financial_exposure,
    activeRecords,
    activeDebtRecords,
  ]);

  // Translated label for a stored exposure, or "" when nothing is selected.
  const exposureLabelFor = (value) => {
    if (!value) return "";
    const match = exposureGroups[0].options.find(
      (option) => option.value === value
    );
    return match ? match.label : value;
  };

  // The debt-type list with its labels translated, as `Dropdown` wants them.
  const debtTypeGroups = useMemo(
    () =>
      DEBT_TYPE_GROUPS.map((group) => ({
        label: t(group.labelKey),
        options: group.options.map((option) => ({
          value: option.value,
          label: t(option.labelKey),
        })),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content]
  );

  // Translated label for a stored debt type, or "" when nothing is selected.
  const debtTypeLabel = (value) => {
    if (!value) return "";
    for (const group of DEBT_TYPE_GROUPS) {
      const match = group.options.find((option) => option.value === value);
      if (match) return t(match.labelKey);
    }
    return value;
  };

  const renderCurrencySelect = (field, errorKey) => (
    <select
      className={`${styles.select} ${errorKey ? invalidClass(errorKey) : ""}`}
      value={evaluation[field] || defaultCurrency}
      disabled={disabled}
      onChange={(e) => {
        update({ [field]: e.target.value });
        if (errorKey) clearFieldError(errorKey, e.target.value);
      }}
    >
      {CURRENCY_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  /*
    The saved currency, or the language default when there is none — and also
    when the stored code is not one we offer (older records carry the "gpb"
    typo from My Account/activateAccount.jsx), so the lookup cannot come back
    empty.
  */
  const currency = useMemo(() => {
    const find = (code) => CURRENCY_OPTIONS.find((c) => c.value === code);
    return find(evaluation.currency) || find(defaultCurrency);
  }, [evaluation.currency, defaultCurrency]);
  /*
    Always resolves: with nothing selected, `currency` above falls back to the
    language default, so the chart, the history tables and the value pills are
    labelled in EUR for French and USD for English rather than in nothing.
  */
  /*
    Display currency for the chart and the two history tables.

    Previously the form's currency picker relabelled every past record, which
    silently misrepresented them — a value entered in EUR would suddenly read as
    USD. Each record now carries its own currency, and this selector converts
    for display only; nothing stored is rewritten.

    It starts on the asset's own currency and follows it until the user picks
    something else, after which their choice sticks.
  */
  const [displayCurrency, setDisplayCurrency] = useState(currency.value);
  const [displayTouched, setDisplayTouched] = useState(false);
  useEffect(() => {
    if (!displayTouched) setDisplayCurrency(currency.value);
  }, [currency.value, displayTouched]);

  /*
    Display currency for the chart and both history tables. Converts on the fly
    using the backend's rate table; the stored values are never rewritten, so
    switching back and forth is lossless.

    Defined once and placed in whichever header is on screen — the history's
    when it is open, the chart's when it is not. It belongs to the whole block
    below it, so it sits on the first heading of that block rather than staying
    on the chart while the history opens above it.

    The label sits beside the field rather than above it, and says what the
    control does rather than just "Currency": on its own a bare currency code
    beside a chart reads as a statement of what the figures ARE, when it is
    actually a control for what they are shown in.
  */
  const renderDisplayCurrency = () => (
    <div className={styles.display_currency_field}>
      <div className={`${styles.label} ${styles.display_currency_label}`}>
        {t("assetEvaluationText78")}
      </div>
      <select
        className={`${styles.select} ${styles.display_currency_select}`}
        value={displayCurrency}
        onChange={(event) => {
          setDisplayTouched(true);
          setDisplayCurrency(event.target.value);
        }}
        aria-label={t("assetEvaluationText78")}
      >
        {CURRENCY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  // USD-based rate table; null until it arrives, and on failure we simply show
  // amounts as entered rather than blocking the chart.
  const [rates, setRates] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchCurrencyRates()
      .then((data) => {
        if (!cancelled) setRates(data?.rates || null);
      })
      .catch(() => {
        if (!cancelled) setRates(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayCurrencyOption = useMemo(
    () =>
      CURRENCY_OPTIONS.find((option) => option.value === displayCurrency) ||
      currency,
    [displayCurrency, currency]
  );
  const currencyCode = displayCurrencyOption.label;

  /*
    Converts a series into the display currency. A record saved before records
    carried a currency falls back to the asset's, and an unknown currency is
    left as-is rather than rendered as NaN.
  */
  /*
    Sorted before converting, so the history tables and the chart drawn from
    them read oldest to newest by the date ON the reading — the order the
    valuations actually happened, rather than the order they were typed in.
  */
  const toDisplay = (list) =>
    sortRecordsByDate(list).map((record) => ({
      ...record,
      value: convertAmount(
        record?.value,
        record?.currency || currency.value,
        displayCurrency,
        rates
      ),
    }));

  const displayRecords = useMemo(
    () => toDisplay(activeRecords),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeRecords, displayCurrency, rates, currency.value]
  );
  const displayDebtRecords = useMemo(
    () => toDisplay(activeDebtRecords),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeDebtRecords, displayCurrency, rates, currency.value]
  );
  /*
    Through the same conversion, so a proposed reading is shown in the same
    currency as the rows above it -- comparing them is the whole point.
  */
  const displayProposedRecords = useMemo(
    () => toDisplay(proposedRecords),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proposedRecords, displayCurrency, rates, currency.value]
  );
  const displayProposedDebtRecords = useMemo(
    () => toDisplay(proposedDebtRecords),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proposedDebtRecords, displayCurrency, rates, currency.value]
  );

  /*
    The chart carries both series on one signed axis: assets in green above the
    zero line, debts in red below it. The debt line only appears once the asset
    is marked as carrying a debt — otherwise there is nothing for it to show and
    a red line would only be confusing.

    Debt pills are drawn under their points and asset pills over theirs, so the
    two sets of value labels stay out of each other's way.
  */
  const chartSeries = useMemo(() => {
    const list = [
      {
        key: "asset",
        records: displayRecords,
        label: t("assetEvaluationText7"),
        color: ASSET_SERIES_COLOR,
        gradientId: "ostreon_eval_area_asset",
      },
    ];
    /*
      The debt line shows whenever the asset HAS debts, not when the debt
      switch happens to be on. The switch is an input control and opens off on
      every visit, so gating the chart on it hid the debt series from anyone who
      did not re-open the section.
    */
    if (displayDebtRecords.length > 0) {
      list.push({
        key: "debt",
        records: displayDebtRecords,
        label: t("assetEvaluationText55"),
        color: DEBT_SERIES_COLOR,
        gradientId: "ostreon_eval_area_debt",
        labelsBelow: true,
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRecords, displayDebtRecords, content]);

  /*
    Track is 24 x 14.6 with fill #34C759 in the design (measured from
    associated_debt.svg via getBBox), so: 24 x 15 track, 12px thumb inset
    1.5px, giving 9px of travel.
  */
  const switchSx = {
    width: 24,
    height: 15,
    padding: 0,
    display: "flex",
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: "1.5px",
      transitionDuration: "200ms",
      "&.Mui-checked": {
        transform: "translateX(9px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#34C759",
          opacity: 1,
          border: 0,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 12,
      height: 12,
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
    },
    "& .MuiSwitch-track": {
      borderRadius: 15 / 2,
      backgroundColor: "#E5E5EA",
      opacity: 1,
    },
    /*
      At this size the control alone is far below a comfortable tap target, so
      stretch the (invisible) checkbox beyond the visual switch. Keeps the
      design's dimensions without making it fiddly to hit on mobile.
    */
    "& .MuiSwitch-input": {
      width: "300%",
      height: "300%",
      left: "-100%",
      top: "-100%",
    },
  };

  /*
    Returns the two grid cells (label, control) rather than a wrapping row —
    both switches share one grid so their columns line up. See `.switch_grid`.
  */
  const renderSwitchRow = (labelKey, field, lockedOn = false) => (
    <React.Fragment key={field}>
      {/*
        A switch has no box to outline, so the label carries the colour and
        the review pill sits beneath it -- still immediately above the
        control it is about, in the same grid cell.
      */}
      <div className={`${styles.switch_label} ${proposalMark(field, "marked")}`}>
        {renderProposal(field, t(labelKey), <span>{t(labelKey)}</span>)}
      </div>
      <div className={styles.switch_control}>
        <span
          className={`${styles.switch_side} ${
            !evaluation[field] ? styles.switch_side_active : ""
          }`}
        >
          {t("assetEvaluationText17")}
        </span>
        <Switch
          sx={switchSx}
          checked={Boolean(evaluation[field])}
          /*
            `lockedOn` holds the debt switch on once the asset has debt records.
            Turning it off clears the debt description, which those records
            would then be readings of nothing — so the option is withdrawn
            rather than offered and refused. Delete the records first.
          */
          disabled={disabled || lockedOn}
          onChange={(e) => update({ [field]: e.target.checked })}
          inputProps={{ "aria-label": t(labelKey) }}
        />
        <span
          className={`${styles.switch_side} ${
            evaluation[field] ? styles.switch_side_active : ""
          }`}
        >
          {t("assetEvaluationText18")}
        </span>
      </div>
    </React.Fragment>
  );

  return (
    <div className={styles.section} ref={sectionRef}>
      <div className={styles.header}>
        <div className={styles.title}>{t("assetEvaluationText1")}</div>
        <Info message={t("assetEvaluationText2")} />
        {/* Expanded: "Hide" sits beside the title. Collapsed: the call to
            action sits on its own line below it (see below). */}
        {expanded && (
          <button
            type="button"
            className={styles.toggle_link}
            /* Reveals what is already shared -- allowed when read-only. */
            data-viewonly-allow
            onClick={() => setExpanded(false)}
            aria-expanded={true}
          >
            {t("assetEvaluationText4")}
          </button>
        )}
      </div>

      {!expanded && (
        <button
          type="button"
          className={styles.collapsed_trigger}
          /*
            Opens the evaluation panel. Allowed when read-only: a partner whose
            customer shared their evaluations has to be able to see them, and
            unfolding the panel reveals nothing that was not already sent.
          */
          data-viewonly-allow
          onClick={() => setExpanded(true)}
          aria-expanded={false}
        >
          <span className={styles.collapsed_trigger_text}>
            {t("assetEvaluationText3")}
          </span>
          <DownOutlined />
        </button>
      )}

      {expanded && (
        <div className={styles.panel}>
          <div
            className={`${styles.exposure_row} ${
              isCustomExposure ? styles.exposure_row_custom : ""
            }`}
          >
            <div className={styles.exposure_field}>
              {renderProposal(
                "financial_exposure",
                t("assetEvaluationText5"),
                <div className={styles.label}>{t("assetEvaluationText5")}</div>
              )}
              {/*
                The same dropdown the debt type uses, not a native <select>.

                A native one on macOS is a popup menu: it puts the chosen row on
                top of the closed field, so the list lands somewhere different
                depending on what is selected. It also draws its options in OS
                colours, which is why they read black on one machine and grey or
                white-on-dark on another. This one always opens below the field
                and is styled by the app, so it looks the same everywhere.
              */}
              <Dropdown
                value={evaluation.financial_exposure || ""}
                onChange={(next) => {
                  // Drop any half-typed label when leaving the custom option.
                  clearFieldError("main:financial_exposure", next);
                  update({
                    financial_exposure: next,
                    ...(next === FINANCIAL_EXPOSURE_CUSTOM
                      ? {}
                      : { financial_exposure_custom_label: "" }),
                  });
                }}
                groups={exposureGroups}
                placeholder={t("assetEvaluationText16")}
                disabled={disabled}
                className={`${invalidClass(
                  "main:financial_exposure"
                )} ${proposalMark("financial_exposure")}`}
                ariaLabel={t("assetEvaluationText5")}
                /* Readable on hover when the label outruns the field. */
                title={exposureLabelFor(evaluation.financial_exposure)}
              />
            </div>

            {isCustomExposure && (
              <div className={styles.exposure_field}>
                {renderProposal(
                  "financial_exposure_custom_label",
                  t("assetEvaluationText25"),
                  <div className={styles.label}>
                    {t("assetEvaluationText25")}
                  </div>
                )}
                <input
                  type="text"
                  className={`${styles.field} ${invalidClass(
                    "main:custom_label"
                  )} ${proposalMark("financial_exposure_custom_label")}`}
                  placeholder={t("assetEvaluationText26")}
                  value={evaluation.financial_exposure_custom_label || ""}
                  disabled={disabled}
                  onChange={(e) => {
                    update({ financial_exposure_custom_label: e.target.value });
                    clearFieldError("main:custom_label", e.target.value);
                  }}
                />
              </div>
            )}
          </div>

          <div className={styles.value_row}>
            <div className={styles.cell}>
              {renderProposal(
                "evaluation_date",
                t("assetEvaluationText6"),
                <div className={styles.label}>{t("assetEvaluationText6")}</div>
              )}
              {/*
                Ours, not the browser's: a native date input is drawn in the
                BROWSER's locale and reads DD/MM even with the site in English,
                and neither `lang` on the input nor on an ancestor moves it.
                Same field class, same box; only the format is ours.
              */}
              <ReportDateField
                className={`${styles.field} ${styles.field_date} ${invalidClass(
                  "main:evaluation_date"
                )} ${proposalMark("evaluation_date")}`}
                value={evaluation.evaluation_date}
                disabled={disabled}
                onChange={(next) => {
                  update({ evaluation_date: next });
                  clearFieldError("main:evaluation_date", next);
                }}
                french={websiteCurrentLang?.label === "fr"}
                ariaLabel={t("assetEvaluationText6")}
              />
            </div>

            <div className={styles.cell}>
              {renderProposal(
                "asset_value",
                t("assetEvaluationText7"),
                <div className={styles.label}>{t("assetEvaluationText7")}</div>
              )}
              <input
                type="text"
                inputMode="numeric"
                className={`${styles.field} ${invalidClass(
                  "main:asset_value"
                )} ${proposalMark("asset_value")}`}
                placeholder="0"
                value={displayAmount(evaluation.asset_value)}
                disabled={disabled}
                onChange={(e) => {
                  const next = parseAmount(e.target.value);
                  update({ asset_value: next });
                  clearFieldError("main:asset_value", next);
                }}
              />
            </div>

            <div className={styles.cell}>
              {renderProposal(
                "currency",
                t("assetEvaluationText8"),
                <div className={styles.label}>{t("assetEvaluationText8")}</div>
              )}
              {renderCurrencySelect("currency", "main:currency")}
            </div>
          </div>

          {/*
            Records the reading without saving or closing the asset.

            Under the currency and aligned to it, not in the panel's corner: it
            belongs to the four fields above it and to nothing else, and the
            asset's own Save is a long way down the modal. Reading order should
            make it obvious which of the two this one is.
          */}
          {onSaveReading && (
            <div className={styles.reading_save_row}>
              <button
                type="button"
                className={styles.reading_save}
                disabled={disabled}
                onClick={saveReading}
              >
                {t("assetEvaluationText85")}
              </button>
            </div>
          )}

          {dataEntries.map((entry) => {
            const invalid = Boolean(ownershipErrors[entry.id]);
            // Types the asset's OTHER blocks already use — see takenEntryTypes.
            const taken = takenEntryTypes(dataEntries, entry.id);
            return (
              <div key={entry.id}>
                <div className={styles.section_divider} />
                <div className={styles.label}>{t("assetEvaluationText35")}</div>
                <div className={styles.nf_block}>
                  <button
                    type="button"
                    className={styles.nf_delete}
                    title={t("assetEvaluationText42")}
                    aria-label={t("assetEvaluationText42")}
                    disabled={disabled}
                    onClick={() => removeDataEntry(entry.id)}
                  />

                  <div className={styles.nf_type}>
                    <select
                      className={`${styles.select} ${styles.nf_field} ${
                        entry.type ? "" : styles.select_placeholder
                      }`}
                      value={entry.type}
                      disabled={disabled}
                      onChange={(e) =>
                        changeEntryType(entry.id, e.target.value)
                      }
                    >
                      <option value="">{t("assetEvaluationText16")}</option>
                      {DATA_ENTRY_TYPES.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          /*
                            Never the block's OWN type: an asset saved before
                            this rule could hold two of a kind, and greying out
                            the value a select is currently showing reads as a
                            glitch rather than as a rule.
                          */
                          disabled={
                            option.value !== entry.type &&
                            taken.has(option.value)
                          }
                        >
                          {t(option.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {entry.type === "other_owners" && (
                    <div className={styles.nf_grid}>
                      {entry.owners.map((owner, i) => (
                        <React.Fragment key={owner.id}>
                          {/* My Ownership only sits beside the first owner. */}
                          <div
                            className={`${styles.nf_cell} ${styles.nf_cell_mine} ${
                              i === 0 ? "" : styles.nf_cell_spacer
                            }`}
                          >
                            {i === 0 && (
                              <>
                                <div className={styles.label}>
                                  {t("assetEvaluationText37")}
                                </div>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className={`${styles.field} ${styles.nf_field} ${
                                    invalid ? styles.field_invalid : ""
                                  } ${invalidClass(
                                    `entry:${entry.id}:my_ownership`
                                  )}`}
                                  placeholder="0%"
                                  value={displayPercent(entry.my_ownership)}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    handlePercentChange(e, (v) => {
                                      patchEntry(entry.id, { my_ownership: v });
                                      clearFieldError(
                                        `entry:${entry.id}:my_ownership`,
                                        v
                                      );
                                    })
                                  }
                                />
                              </>
                            )}
                          </div>

                          <div className={`${styles.nf_cell} ${styles.nf_cell_owner}`}>
                            <div className={styles.label}>
                              {`${t("assetEvaluationText38")}${i + 2}`}
                            </div>
                            {/*
                              Custom replaces the dropdown with a field to name
                              the owner in, the way picking Custom replaces the
                              asset-type dropdown. The ✕ goes back to the list.

                              The row's own value is what holds the mode, so
                              nothing extra has to be tracked per row: the
                              sentinel means "being named", and the name that
                              replaces it on save means "named".

                              `owner_custom_label` lives only between typing and
                              saving — resolveOwnersSelection turns it into the
                              row's owner and drops it, so it never reaches the
                              stored asset.
                            */}
                            {legacyName(owner.owner) === OWNER_CUSTOM ? (
                              <div className={styles.custom_owner_wrap}>
                                <input
                                  autoFocus
                                  type="text"
                                  className={`${styles.select} ${
                                    styles.nf_field
                                  } ${
                                    ownerErrors[owner.id]
                                      ? styles.field_invalid
                                      : ""
                                  }`}
                                  placeholder={t("assetEvaluationText24")}
                                  value={owner.owner_custom_label || ""}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    patchOwner(entry.id, owner.id, {
                                      owner_custom_label: e.target.value,
                                    })
                                  }
                                />
                                {/* Back to the dropdown, discarding the name. */}
                                <button
                                  type="button"
                                  className={styles.custom_owner_clear}
                                  aria-label={t("assetEvaluationText24")}
                                  disabled={disabled}
                                  onClick={() =>
                                    patchOwner(entry.id, owner.id, {
                                      owner: "",
                                      owner_custom_label: "",
                                    })
                                  }
                                />
                              </div>
                            ) : (
                            <select
                              className={`${styles.select} ${styles.nf_field} ${
                                owner.owner ? "" : styles.select_placeholder
                              } ${
                                ownerErrors[owner.id] ? styles.field_invalid : ""
                              }`}
                              value={legacyName(owner.owner)}
                              disabled={disabled}
                              onChange={(e) => {
                                patchOwner(entry.id, owner.id, {
                                  owner: e.target.value,
                                });
                                // Clear the flag as soon as one is chosen.
                                if (e.target.value && ownerErrors[owner.id]) {
                                  setOwnerErrors((prev) => ({
                                    ...prev,
                                    [owner.id]: false,
                                  }));
                                }
                              }}
                            >
                              {/*
                                `disabled hidden`, so "Owner One" is what the
                                closed field reads and nothing more.

                                A bare `<option value="">` is a real row: the
                                list opened with "Owner One" sitting above the
                                actual people, offering itself as a choice, and
                                picking it put the row back to empty -- which
                                the save then refuses, since an owner is
                                required. It is a prompt, not an owner.

                                Both words are needed. `disabled` alone greys
                                it and leaves it listed; `hidden` alone leaves
                                it selectable. Together the field still shows
                                it while empty, which is the whole point.
                              */}
                              <option value="" disabled hidden>
                                {ownerPlaceholder(i)}
                              </option>
                              {(() => {
                                /*
                                  Anyone already chosen in another row of this
                                  block is dropped, except this row's own
                                  selection which has to stay or the select
                                  renders blank. "Custom" is exempt — it stands
                                  for someone in neither list, so several rows
                                  may use it.
                                */
                                const current = legacyName(owner.owner);
                                const taken = new Set(
                                  (entry.owners || [])
                                    .filter((other) => other.id !== owner.id)
                                    .map((other) => legacyName(other.owner))
                                    .filter((v) => v && v !== "custom")
                                );
                                const available = ownerOptions.filter(
                                  (name) => name === current || !taken.has(name)
                                );
                                /*
                                  A saved name that is no longer in either list
                                  (renamed or deleted) still has to appear, or
                                  the select would blank and the row would be
                                  lost on the next save.
                                */
                                const missing =
                                  current &&
                                  current !== "custom" &&
                                  !ownerOptions.includes(current);
                                return (
                                  <>
                                    {available.map((name) => (
                                      <option key={name} value={name}>
                                        {name}
                                      </option>
                                    ))}
                                    {missing && (
                                      <option value={current}>{current}</option>
                                    )}
                                  </>
                                );
                              })()}
                              <option value={OWNER_CUSTOM}>
                                {t("assetEvaluationText24")}
                              </option>
                            </select>
                            )}
                          </div>

                          <div className={`${styles.nf_cell} ${styles.nf_cell_pct}`}>
                            <div className={styles.label}>
                              {t("assetEvaluationText39")}
                            </div>
                            <input
                              type="text"
                              inputMode="decimal"
                              className={`${styles.field} ${styles.nf_field} ${
                                invalid ? styles.field_invalid : ""
                              } ${invalidClass(
                                `owner:${owner.id}:ownership`
                              )}`}
                              placeholder="0%"
                              value={displayPercent(owner.ownership)}
                              disabled={disabled}
                              onChange={(e) =>
                                handlePercentChange(e, (v) => {
                                  patchOwner(entry.id, owner.id, {
                                    ownership: v,
                                  });
                                  clearFieldError(
                                    `owner:${owner.id}:ownership`,
                                    v
                                  );
                                })
                              }
                            />
                          </div>

                          {/* Only owners beyond the first can be removed. */}
                          <div className={`${styles.nf_cell} ${styles.nf_cell_del}`}>
                            {i > 0 && (
                              <button
                                type="button"
                                className={styles.nf_owner_delete}
                                title={t("assetEvaluationText42")}
                                aria-label={t("assetEvaluationText42")}
                                disabled={disabled}
                                onClick={() => removeOwner(entry.id, owner.id)}
                              />
                            )}
                          </div>
                        </React.Fragment>
                      ))}

                      {entry.owners.some((o) => ownerErrors[o.id]) && (
                        <div className={styles.nf_error}>
                          {t("assetEvaluationText45")}
                        </div>
                      )}

                      {(fieldErrors[`entry:${entry.id}:my_ownership`] ||
                        entry.owners.some(
                          (o) => fieldErrors[`owner:${o.id}:ownership`]
                        )) && (
                        <div className={styles.nf_error}>
                          {t("assetEvaluationText52")}
                        </div>
                      )}

                      {invalid && (
                        <div className={styles.nf_error}>
                          {t("assetEvaluationText41")}
                        </div>
                      )}

                      <button
                        type="button"
                        className={styles.nf_add_owner}
                        disabled={disabled || entry.owners.length >= MAX_OWNERS}
                        onClick={() => addOwner(entry.id)}
                      >
                        <PlusCircleFilled className={styles.add_entry_icon} />
                        <span>{t("assetEvaluationText40")}</span>
                      </button>
                    </div>
                  )}

                  {entry.type === "location" && (
                    <div className={styles.nf_row_simple}>
                      <div className={`${styles.nf_cell} ${styles.nf_cell_country}`}>
                        <div className={styles.label}>
                          {t("assetEvaluationText80")}
                        </div>
                        {/*
                          Two hundred countries is past the point a plain select
                          is usable, so this one is searchable — see CountryPicker.
                        */}
                        <CountryPicker
                          value={entry.country}
                          placeholder={t("assetEvaluationText81")}
                          disabled={disabled}
                          ariaLabel={t("assetEvaluationText80")}
                          invalid={invalidClass(`entry:${entry.id}:country`)}
                          onChange={(name) => {
                            patchEntry(entry.id, { country: name });
                            clearFieldError(`entry:${entry.id}:country`, name);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {INCOME_ENTRY_TYPES.includes(entry.type) && (
                    <div className={styles.nf_row_simple}>
                      <div className={`${styles.nf_cell} ${styles.nf_cell_amount}`}>
                        <div className={styles.label}>
                          {t("assetEvaluationText84")}
                        </div>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${styles.field} ${styles.nf_field} ${invalidClass(
                            `entry:${entry.id}:amount`
                          )}`}
                          value={displayAmount(entry.amount)}
                          disabled={disabled}
                          onChange={(e) => {
                            // Digits and one separator only, as the value fields take.
                            const next = e.target.value.replace(/[^\d.]/g, "");
                            patchEntry(entry.id, { amount: next });
                            clearFieldError(`entry:${entry.id}:amount`, next);
                          }}
                        />
                      </div>
                      <div className={`${styles.nf_cell} ${styles.nf_cell_curr}`}>
                        <div className={styles.label}>
                          {t("assetEvaluationText8")}
                        </div>
                        {/* The same currency list every other amount here uses. */}
                        <select
                          className={`${styles.select} ${styles.nf_field} ${invalidClass(
                            `entry:${entry.id}:currency`
                          )}`}
                          value={entry.currency || defaultCurrency}
                          disabled={disabled}
                          onChange={(e) => {
                            patchEntry(entry.id, { currency: e.target.value });
                            clearFieldError(`entry:${entry.id}:currency`, e.target.value);
                          }}
                        >
                          {CURRENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {entry.type === "custom" && (
                    <div className={styles.nf_cf_grid}>
                      {(entry.custom_fields || []).map((field) => (
                        <React.Fragment key={field.id}>
                          <div
                            className={`${styles.nf_cell} ${styles.nf_cell_title}`}
                          >
                            <div className={styles.label}>
                              {t("assetEvaluationText47")}
                            </div>
                            <input
                              type="text"
                              className={`${styles.field} ${
                                styles.nf_field
                              } ${invalidClass(`cf:${field.id}:title`)}`}
                              placeholder={t("assetEvaluationText49")}
                              value={field.title}
                              disabled={disabled}
                              onChange={(e) => {
                                patchCustomField(entry.id, field.id, {
                                  title: e.target.value,
                                });
                                clearFieldError(
                                  `cf:${field.id}:title`,
                                  e.target.value
                                );
                                clearFieldError(`cf:${field.id}:duplicate`, e.target.value);
                              }}
                            />
                          </div>

                          <div
                            className={`${styles.nf_cell} ${styles.nf_cell_value}`}
                          >
                            <div className={styles.label}>
                              {t("assetEvaluationText48")}
                            </div>
                            <input
                              type="text"
                              className={`${styles.field} ${
                                styles.nf_field
                              } ${invalidClass(`cf:${field.id}:value`)}`}
                              placeholder={t("assetEvaluationText50")}
                              value={field.value}
                              disabled={disabled}
                              onChange={(e) => {
                                patchCustomField(entry.id, field.id, {
                                  value: e.target.value,
                                });
                                clearFieldError(
                                  `cf:${field.id}:value`,
                                  e.target.value
                                );
                              }}
                            />
                          </div>

                          {/* A lone custom field cannot be removed. */}
                          <div
                            className={`${styles.nf_cell} ${styles.nf_cell_del}`}
                          >
                            {(entry.custom_fields || []).length > 1 && (
                              <button
                                type="button"
                                className={styles.nf_owner_delete}
                                title={t("assetEvaluationText42")}
                                aria-label={t("assetEvaluationText42")}
                                disabled={disabled}
                                onClick={() =>
                                  removeCustomField(entry.id, field.id)
                                }
                              />
                            )}
                          </div>
                        </React.Fragment>
                      ))}

                      {(entry.custom_fields || []).some(
                        (f) =>
                          fieldErrors[`cf:${f.id}:title`] ||
                          fieldErrors[`cf:${f.id}:value`]
                      ) && (
                        <div className={styles.nf_error}>
                          {/*
                            A duplicate title needs naming explicitly — "fill in
                            all the fields shown" would be misleading when every
                            field is filled.
                          */}
                          {(entry.custom_fields || []).some(
                            (f) => fieldErrors[`cf:${f.id}:duplicate`]
                          )
                            ? t("assetEvaluationText76")
                            : t("assetEvaluationText52")}
                        </div>
                      )}

                      <button
                        type="button"
                        className={styles.nf_add_owner}
                        disabled={disabled}
                        onClick={() => addCustomField(entry.id)}
                      >
                        <PlusCircleFilled className={styles.add_entry_icon} />
                        <span>{t("assetEvaluationText51")}</span>
                      </button>
                    </div>
                  )}

                  {/*
                    This field's own Save, inside the block it commits rather
                    than under it: the purple panel is what marks where one
                    field stops and the next begins, so a button sitting
                    outside it reads as belonging to the panel as a whole.

                    Each block is filled in on its own, so each is saved on its
                    own rather than waiting on whichever other one is still
                    half-typed.
                  */}
                  {onSaveEntry && (
                    <div className={styles.nf_save_row}>
                      <button
                        type="button"
                        className={styles.reading_save}
                        disabled={disabled}
                        onClick={() => saveEntry(entry.id)}
                      >
                        {t("assetEvaluationText85")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <button
            type="button"
            className={styles.add_entry}
            disabled={disabled}
            onClick={addDataEntry}
          >
            <PlusCircleFilled className={styles.add_entry_icon} />
            <span>{t("assetEvaluationText34")}</span>
          </button>

          <div className={styles.section_divider} />

          <div className={styles.switch_grid}>
            {renderSwitchRow("assetEvaluationText9", "is_liquid")}
            {renderSwitchRow(
              "assetEvaluationText10",
              "has_associated_debt",
              (activeDebtRecords || []).length > 0
            )}
          </div>

          {/* Revealed by the "associated debt" switch above. */}
          {evaluation.has_associated_debt && (
            <div ref={debtRef}>
              <div className={`${styles.debt_row} ${styles.debt_row_lead}`}>
                <div className={styles.cell}>
                  {renderProposal(
                    "debt_type",
                    t("assetEvaluationText27"),
                    <div className={`${styles.label} ${styles.label_required}`}>
                      {t("assetEvaluationText27")}
                    </div>
                  )}
                  {/*
                    A native <select> would open with the chosen row over the
                    field, so the panel lands somewhere different depending on
                    what is selected. This one always drops below the field.
                    The group headings are not selectable.
                  */}
                  <Dropdown
                    value={evaluation.debt_type || ""}
                    onChange={(next) => {
                      update({ debt_type: next });
                      clearFieldError("debt:type", next);
                    }}
                    groups={debtTypeGroups}
                    placeholder={t("assetEvaluationText16")}
                    disabled={disabled}
                    className={`${invalidClass("debt:type")} ${proposalMark(
                      "debt_type"
                    )}`}
                    ariaLabel={t("assetEvaluationText27")}
                    /* Readable on hover when the label outruns the column. */
                    title={debtTypeLabel(evaluation.debt_type)}
                  />
                </div>

                <div className={styles.cell}>
                  {renderProposal(
                    "debt_amount",
                    t("assetEvaluationText28"),
                    <div className={`${styles.label} ${styles.label_required}`}>
                      {t("assetEvaluationText28")}
                    </div>
                  )}
                  <input
                    type="text"
                    inputMode="numeric"
                    className={`${styles.field} ${invalidClass(
                      "debt:amount"
                    )} ${proposalMark("debt_amount")}`}
                    placeholder="-0"
                    value={displayAmount(parseDebtAmount(evaluation.debt_amount))}
                    disabled={disabled}
                    /*
                      The minus is pinned, so the caret is pushed back to the
                      end after every keystroke — otherwise typing into an empty
                      field would leave it wedged between the sign and the digits
                      and the next character would land in the wrong place.
                    */
                    onChange={(e) => {
                      const input = e.currentTarget;
                      const next = parseDebtAmount(input.value);
                      update({ debt_amount: next });
                      clearFieldError("debt:amount", next);
                      window.requestAnimationFrame(() => {
                        if (document.activeElement === input) {
                          const end = input.value.length;
                          input.setSelectionRange(end, end);
                        }
                      });
                    }}
                  />
                </div>

                <div className={styles.cell}>
                  {renderProposal(
                    "debt_currency",
                    t("assetEvaluationText8"),
                    <div className={styles.label}>
                      {t("assetEvaluationText8")}
                    </div>
                  )}
                  {renderCurrencySelect("debt_currency", "debt:currency")}
                </div>
              </div>

              {/* Third column intentionally empty, as in the design. */}
              <div className={styles.debt_row}>
                <div className={styles.cell}>
                  {renderProposal(
                    "debt_annual_interest",
                    t("assetEvaluationText29"),
                    <div className={styles.label}>
                      {t("assetEvaluationText29")}
                    </div>
                  )}
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${styles.field} ${styles.field_percent} ${invalidClass("debt:interest")} ${proposalMark("debt_annual_interest")}`}
                    placeholder="0%"
                    value={displayPercent(evaluation.debt_annual_interest)}
                    disabled={disabled}
                    onChange={(e) =>
                      handlePercentChange(e, (v) => {
                        update({ debt_annual_interest: v });
                        clearFieldError("debt:interest", v);
                      })
                    }
                  />
                </div>

                <div className={styles.cell}>
                  {renderProposal(
                    "debt_payoff_date",
                    t("assetEvaluationText30"),
                    <div className={styles.label}>
                      {t("assetEvaluationText30")}
                    </div>
                  )}
                  <ReportDateField
                    className={`${styles.field} ${styles.field_date} ${invalidClass(
                      "debt:payoff"
                    )} ${proposalMark("debt_payoff_date")}`}
                    value={evaluation.debt_payoff_date}
                    disabled={disabled}
                    onChange={(next) => {
                      update({ debt_payoff_date: next });
                      clearFieldError("debt:payoff", next);
                    }}
                    french={websiteCurrentLang?.label === "fr"}
                    ariaLabel={t("assetEvaluationText30")}
                  />
                </div>

                {/*
                  The debt's own Save, in the row's third column -- the one the
                  currency occupies above it -- so it sits beside the last
                  field it commits instead of on a line of its own.

                  Bottom-aligned: the cells beside it each carry a label above
                  their input, and this one has none to carry.

                  A shortcut, not a gate: the asset's own Save writes these
                  figures too. This one commits them without saving or closing
                  the asset -- and it is the only Save that will ASK for them,
                  so a half-filled debt never blocks the asset's.
                */}
                <div className={`${styles.cell} ${styles.debt_save_cell}`}>
                  {onSaveDebt && debtSaveButton}
                </div>
              </div>

            </div>
          )}

          {/*
            The same Save again, for the one case the row above cannot cover:
            the switch turned OFF over an asset that still HOLDS a debt. The
            fields went with the switch, so the button has no row to sit in --
            but turning the switch off is itself a debt change, and this
            commits it on the spot the way the in-row button commits the
            fields. The asset's own Save would clear the debt too; this is the
            shortcut, not the only way.
          */}
          {onSaveDebt && !evaluation.has_associated_debt && debtCommitted && (
            <div className={styles.reading_save_row}>{debtSaveButton}</div>
          )}

          {/*
            Debt Input History stands on its own, outside the switch. It is the
            only place a debt record can be deleted, so hiding it behind a
            control that opens off would leave existing debts unreachable. It
            appears whenever the asset has debts, or while one is being entered.
          */}
          {((activeDebtRecords || []).length > 0 ||
            evaluation.has_associated_debt) && (
            <>
              {/*
                The debt's own record history, laid out exactly like the
                "Asset Evaluation Over Time" heading below: title on the left,
                the same link on the right, and the same table underneath — only
                the series and the value column's heading differ.
              */}
              <div className={styles.chart_header}>
                <span className={styles.chart_title}>
                  {t("assetEvaluationText54")}
                </span>
                {debtHistoryExpanded ? (
                  <button
                    type="button"
                    className={styles.toggle_link}
                    /* Reveals what is already shared -- allowed when read-only. */
                    data-viewonly-allow
                    onClick={() => setDebtHistoryExpanded(false)}
                    aria-expanded={true}
                  >
                    {t("assetEvaluationText4")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.history_link}
                    /* Reveals what is already shared -- allowed when read-only. */
                    data-viewonly-allow
                    onClick={() => {
                      setDebtHistoryExpanded(true);
                      if (onOpenHistory) onOpenHistory();
                    }}
                    aria-expanded={false}
                  >
                    {t("assetEvaluationText12")}
                  </button>
                )}
              </div>

              {/*
                A suggested reading shows as a marked row in the table below,
                but a row cannot be answered -- so the switch sits under the
                heading, and it covers the whole history because that is how
                the field is stored and therefore how it is proposed.
              */}
              {debtHistoryExpanded &&
                proposedFields.includes("asset_debt_records") && (
                  <div className={styles.history_review_row}>
                    {renderProposal(
                      "asset_debt_records",
                      t("assetEvaluationText54")
                    )}
                  </div>
                )}
              {debtHistoryExpanded &&
                renderHistoryTable({
                  records: displayDebtRecords,
                  proposed: displayProposedDebtRecords,
                  markKeys: proposedDebtRecordKeys,
                  valueHeader: `${t("assetEvaluationText55")} (${currencyCode})`,
                  emptyText: t("assetEvaluationText56"),
                  onDelete: handleDeleteDebtRecord,
                })}
            </>
          )}

          {Object.keys(fieldErrors).some(
            (key) =>
              fieldErrors[key] &&
              (key.startsWith("main:") || key.startsWith("debt:"))
          ) && (
            <div className={styles.panel_error}>
              {t("assetEvaluationText52")}
            </div>
          )}

          {/*
            One divider separates the switches (and the debt block, when shown)
            from everything below. Absent when neither the debt fields nor the
            history are on — matching the original design.
          */}
          {(evaluation.has_associated_debt ||
            (activeDebtRecords || []).length > 0 ||
            historyExpanded) && (
            <div className={styles.divider} />
          )}

          {historyExpanded && (
            <>
              <div className={styles.history_header}>
                <span className={styles.history_title}>
                  {t("assetEvaluationText31")}
                </span>
                <button
                  type="button"
                  className={styles.toggle_link}
                  /* Reveals what is already shared -- allowed when read-only. */
                  data-viewonly-allow
                  onClick={() => setHistoryExpanded(false)}
                  aria-expanded={true}
                >
                  {t("assetEvaluationText4")}
                </button>
                {renderDisplayCurrency()}
              </div>

              {/* See the debt history above: under the heading, not in it. */}
              {proposedFields.includes("asset_evaluation_records") && (
                <div className={styles.history_review_row}>
                  {renderProposal(
                    "asset_evaluation_records",
                    t("assetEvaluationText31")
                  )}
                </div>
              )}
              {renderHistoryTable({
                records: displayRecords,
                proposed: displayProposedRecords,
                markKeys: proposedRecordKeys,
                valueHeader: `${t("assetEvaluationText13")} (${currencyCode})`,
                emptyText: t("assetEvaluationText15"),
                onDelete: handleDeleteRecord,
              })}
            </>
          )}

          <div className={styles.chart_header}>
            <span className={styles.chart_title}>{t("assetEvaluationText11")}</span>
            {/* The link opens the history; the section's own "Hide" closes it. */}
            {!historyExpanded && (
              <button
                type="button"
                className={styles.history_link}
                /* Reveals what is already shared -- allowed when read-only. */
                data-viewonly-allow
                onClick={() => {
                  setHistoryExpanded(true);
                  if (onOpenHistory) onOpenHistory();
                }}
              >
                {t("assetEvaluationText12")}
              </button>
            )}
            {/* Only while the history is closed; it moves up to the
                history's own header when that opens. */}
            {!historyExpanded && renderDisplayCurrency()}
          </div>

          <div className={styles.chart_card}>
            <div className={styles.chart_scroll}>
              <EvaluationChart
                series={chartSeries}
                currencyCode={currencyCode}
                title={`${t("assetEvaluationText13")} (${currencyCode}) ${t(
                  "assetEvaluationText14"
                )}`}
                yAxisLabel={t("assetEvaluationText13")}
                xAxisLabel={t("assetEvaluationText6")}
                langLabel={websiteCurrentLang?.label}
                emptyText={t("assetEvaluationText15")}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default AssetEvaluation;
